import React, { useEffect, useRef, useState } from "react";
import {
    Chart as ChartJS,
    Filler,
    LinearScale,
    LineElement,
    PointElement,
    Tooltip,
} from "chart.js";
import { ChartLine, X } from "lucide-react";
import { Line } from "react-chartjs-2";
import "./StockScanner.css";
import { environment } from "environment/environment";
import useStockApiWebsocket from "service/websocket/StockApiWebsocket";

ChartJS.register(Filler, LinearScale, LineElement, PointElement, Tooltip);

const STOCK_PRE_OPEN_CHART_DATA_URL = `${environment.stockApiBaseUrl}/stock-pre-open-data/chart-data`;
const PRE_OPEN_CHART_LINE_COLOR = "#ff9900";
const PRE_OPEN_CHART_FILL_COLOR = "rgba(255, 153, 0, 0.24)";

const toNumber = (value) => {
    const parsedValue = Number.parseFloat(
        String(value ?? "")
            .replace(/,/g, "")
            .replace("%", "")
    );

    return Number.isFinite(parsedValue) ? parsedValue : null;
};

const formatNumber = (value) => {
    const numericValue = toNumber(value);

    if (numericValue == null) {
        return value ?? "--";
    }

    return numericValue.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const SYMBOL_SERIES_SUFFIX_SET = new Set(["EQ", "BE", "BZ", "BL", "SM", "ST"]);

const normalizeSymbol = (value) => {
    const rawValue = String(value ?? "").trim().toUpperCase();

    if (!rawValue) {
        return "";
    }

    const exchangeTrimmedValue = rawValue.includes(":")
        ? rawValue.split(":").pop()?.trim() ?? ""
        : rawValue;
    const symbolParts = exchangeTrimmedValue.split("-");

    if (symbolParts.length > 1) {
        const lastPart = symbolParts[symbolParts.length - 1];

        if (SYMBOL_SERIES_SUFFIX_SET.has(lastPart)) {
            return symbolParts.slice(0, -1).join("-");
        }
    }

    return exchangeTrimmedValue;
};

const normalizeScannerKey = (value) => String(value ?? "").trim().toUpperCase();

const MID_CROSS_BUY = "MID_CROSS_BUY";
const MID_CROSS_SELL = "MID_CROSS_SELL";

const MID_CROSS_BUY_TAB = "MID_CROSS_BUY_TAB";
const MID_CROSS_SELL_TAB = "MID_CROSS_SELL_TAB";
const MC_FILTER_TAB = "MC_FILTER_TAB";
const POD_TAB = "POD_TAB";
const MC_BUY_FILTER_TAB = "MC_BUY_FILTER_TAB";
const MC_SELL_FILTER_TAB = "MC_SELL_FILTER_TAB";

const SCANNER_SECTION_TITLE_MAP = {
    [MID_CROSS_BUY]: "MID CROSS BUY",
    [MID_CROSS_SELL]: "MID CROSS SELL",
    [MC_FILTER_TAB]: "MC FILTER",
    [POD_TAB]: "POD",
    [MC_BUY_FILTER_TAB]: "MC BUY FILTER",
    [MC_SELL_FILTER_TAB]: "MC SELL FILTER",
};

const createSectorDetailsMap = (
    sectorData = [],
    isSectorMatch = () => false,
    restrictToSelectedStocks = true
) =>
    sectorData.reduce((accumulator, sector) => {
        const sectorPercent = toNumber(sector?.pChange);

        if (sectorPercent == null || !isSectorMatch(sectorPercent)) {
            return accumulator;
        }

        (sector?.sectorialIndicesStockModelList ?? []).forEach((stock) => {
            const symbol = normalizeSymbol(stock?.symbol);

            if (
                !symbol ||
                (restrictToSelectedStocks && !SECTOR_SELECTED_STOCK_SET.has(symbol)) ||
                accumulator.has(symbol)
            ) {
                return;
            }

            accumulator.set(symbol, {
                sectorName: sector?.index ?? "--",
                sectorPercent,
            });
        });

        return accumulator;
    }, new Map());

const createLotSizeBySymbolMap = (stockDayRangeData = []) =>
    stockDayRangeData.reduce((accumulator, stock) => {
        const symbol = normalizeSymbol(
            stock?.symbol ??
            stock?.stockSymbol ??
            stock?.tradingSymbol ??
            stock?.ticker
        );
        const lotSize =
            stock?.fno_lot_size ??
            stock?.fnoLotSize ??
            stock?.lot_size ??
            stock?.lotSize ??
            stock?.marketLot ??
            stock?.market_lot;

        if (!symbol || lotSize == null || accumulator.has(symbol)) {
            return accumulator;
        }

        accumulator.set(symbol, lotSize);
        return accumulator;
    }, new Map());

const attachSectorDetailsToStocks = (
    stocks = [],
    sectorDetailsBySymbol = new Map(),
    requireSectorDetails = true
) =>
    stocks
        .map((stock) => {
            const sectorDetails = sectorDetailsBySymbol.get(normalizeSymbol(stock.symbol));

            if (!sectorDetails && requireSectorDetails) {
                return null;
            }

            return {
                ...stock,
                sectorName: sectorDetails?.sectorName ?? stock?.sectorName ?? null,
                sectorPercent: sectorDetails?.sectorPercent ?? stock?.sectorPercent ?? null,
            };
        })
        .filter(Boolean);

const getScannerRowsByKey = (scannerDataSource, key) => {
    const normalizedKey = normalizeScannerKey(key);

    if (scannerDataSource instanceof Map) {
        if (Array.isArray(scannerDataSource.get(key))) {
            return scannerDataSource.get(key);
        }

        for (const [entryKey, entryValue] of scannerDataSource.entries()) {
            if (normalizeScannerKey(entryKey) === normalizedKey) {
                return Array.isArray(entryValue) ? entryValue : [];
            }
        }

        return [];
    }

    if (scannerDataSource && typeof scannerDataSource === "object" && !Array.isArray(scannerDataSource)) {
        if (Array.isArray(scannerDataSource[key])) {
            return scannerDataSource[key];
        }

        const matchedObjectKey = Object.keys(scannerDataSource).find(
            (entryKey) => normalizeScannerKey(entryKey) === normalizedKey
        );

        return matchedObjectKey && Array.isArray(scannerDataSource[matchedObjectKey])
            ? scannerDataSource[matchedObjectKey]
            : [];
    }

    return [];
};

const hasScannerEntries = (scannerDataSource) => {
    if (scannerDataSource instanceof Map) {
        return scannerDataSource.size > 0;
    }

    return Boolean(
        scannerDataSource &&
        typeof scannerDataSource === "object" &&
        !Array.isArray(scannerDataSource) &&
        Object.keys(scannerDataSource).length > 0
    );
};

const getFoTopRowsByKey = (foTopGainerLooserData, key) =>
    Array.isArray(foTopGainerLooserData?.[key]?.data)
        ? foTopGainerLooserData[key].data
        : [];

const createSymbolSet = (rows = []) =>
    new Set(
        rows
            .map((row) =>
                normalizeSymbol(
                    row?.symbol ??
                    row?.stockSymbol ??
                    row?.tradingSymbol ??
                    row?.ticker
                )
            )
            .filter(Boolean)
    );

const getRankedSectors = (sectorData = []) =>
    sectorData
        .map((sector) => ({
            sector,
            sectorPercent: toNumber(sector?.pChange),
        }))
        .filter((entry) => entry.sectorPercent != null)
        .sort((firstEntry, secondEntry) =>
            firstEntry.sectorPercent - secondEntry.sectorPercent
        );

const createSectorSymbolSet = (sectors = []) =>
    new Set(
        sectors
            .flatMap((sector) => sector?.sectorialIndicesStockModelList ?? [])
            .map((stock) => normalizeSymbol(stock?.symbol))
            .filter(Boolean)
    );

const createSectorNameSet = (sectors = []) =>
    new Set(
        sectors
            .map((sector) => String(sector?.index ?? "").trim().toUpperCase())
            .filter(Boolean)
    );

const filterStocksByFoTopAndSectors = (
    stocks = [],
    foTopSymbolSet = new Set(),
    sectorSymbolSet = new Set(),
    sectorNameSet = new Set()
) =>
    stocks.filter((stock) => {
        const symbol = normalizeSymbol(stock?.symbol);
        const sectorName = String(stock?.sectorName ?? "").trim().toUpperCase();

        return (
            symbol &&
            foTopSymbolSet.has(symbol) &&
            (sectorSymbolSet.has(symbol) || sectorNameSet.has(sectorName))
        );
    });

const getStockPreOpenRows = (payload) => {
    const candidates = [
        payload?.data,
        payload?.response?.data,
        payload?.data?.data,
        payload,
    ];

    return candidates.find(Array.isArray) ?? [];
};

const normalizeStockPreOpenRows = (payload) =>
    getStockPreOpenRows(payload).map((entry, index) => {
        const metadata = entry?.metadata ?? entry ?? {};
        const detail = entry?.detail ?? {};
        const preOpenMarket = detail?.preOpenMarket ?? entry?.preOpenMarket ?? {};
        const totalTurnover = toNumber(
            metadata?.totalTurnover ?? detail?.totalTurnover ?? entry?.totalTurnover
        );

        return {
            symbol: normalizeSymbol(metadata?.symbol ?? entry?.symbol) || "--",
            chartIndex:
                metadata?.identifier ?? entry?.identifier ??
                `${normalizeSymbol(metadata?.symbol ?? entry?.symbol)}EQN`,
            previousClose:
                detail?.previousClose ?? metadata?.previousClose ?? entry?.previousClose,
            iep: metadata?.iep ?? preOpenMarket?.IEP ?? preOpenMarket?.iep ?? entry?.iep,
            change: detail?.change ?? metadata?.change ?? entry?.change,
            pChange:
                detail?.pChange ?? metadata?.pChange ?? entry?.pChange ?? entry?.percentChange,
            finalPrice:
                preOpenMarket?.finalPrice ?? metadata?.finalPrice ?? detail?.lastPrice ??
                metadata?.lastPrice ?? entry?.finalPrice,
            finalQuantity:
                preOpenMarket?.finalQuantity ?? metadata?.finalQuantity ?? entry?.finalQuantity ??
                preOpenMarket?.totalTradedVolume,
            valueCrores:
                metadata?.valueCrores ?? entry?.valueCrores ??
                (totalTurnover == null ? null : totalTurnover / 10000000),
            preOpenMarket,
            key: `${metadata?.symbol ?? entry?.symbol ?? "pod"}-${index}`,
        };
    });

const formatPreOpenNumber = (value, maximumFractionDigits = 2) => {
    const numericValue = toNumber(value);

    if (numericValue == null) return "--";

    return numericValue.toLocaleString("en-IN", {
        minimumFractionDigits: 0,
        maximumFractionDigits,
    });
};

const formatPreOpenPrice = (value) => {
    const numericValue = toNumber(value);

    if (numericValue == null) return "-";

    return numericValue.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const formatPreOpenQuantity = (value) => {
    const numericValue = toNumber(value);

    if (numericValue == null || numericValue === 0) return "-";

    return numericValue.toLocaleString("en-IN", {
        maximumFractionDigits: 0,
    });
};

const createStockLtpBySymbolMap = (stockRows = []) =>
    (Array.isArray(stockRows) ? stockRows : []).reduce((ltpBySymbol, stock) => {
        const symbol = normalizeSymbol(
            stock?.symbol ?? stock?.stockSymbol ?? stock?.tradingSymbol ?? stock?.ticker
        );
        const ltp = stock?.lp ?? stock?.ltp ?? stock?.lastPrice;

        if (symbol && ltp != null) {
            ltpBySymbol.set(symbol, ltp);
        }

        return ltpBySymbol;
    }, new Map());

const getPreOpenChartPriceRange = (graphData = []) =>
    graphData.reduce((priceRange, point) => {
        const price = Number(point?.[1]);

        if (!Number.isFinite(price)) return priceRange;

        return {
            highest: priceRange.highest == null
                ? price
                : Math.max(priceRange.highest, price),
            lowest: priceRange.lowest == null
                ? price
                : Math.min(priceRange.lowest, price),
        };
    }, { highest: null, lowest: null });

const normalizeScannerStocks = (stockScannerDataList = [], lotSizeBySymbol = new Map()) =>
    stockScannerDataList.map((stock, index) => {
        const symbol = normalizeSymbol(
            stock?.symbol ??
            stock?.stockSymbol ??
            stock?.tradingSymbol ??
            stock?.ticker ??
            "--"
        ) || "--";
        const fnoLotSize =
            stock?.fno_lot_size ??
            stock?.fnoLotSize ??
            stock?.lot_size ??
            stock?.lotSize ??
            stock?.marketLot ??
            stock?.market_lot ??
            lotSizeBySymbol.get(symbol) ??
            "--";

        return {
            symbolCount: stock?.symbolCount ?? stock?.symbol_count ?? stock?.symbolCnt ?? stock?.count ?? "--",
            symbol,
            status: String(stock?.status ?? "").trim().toUpperCase(),
            changePercent: toNumber(stock?.["%_change"] ?? stock?.changePercent),
            createdDate: stock?.createdDate ?? "--",
            createdTime: stock?.createdTime ?? "--",
            volume: stock?.volume,
            triggerDate: stock?.triggerDate ?? "--",
            triggerTime: stock?.triggerTime ?? "--",
            ltp: stock?.ltp ?? stock?.close,
            fnoLotSize,
            __scannerKey: `${stock?.symbol ?? stock?.stockSymbol ?? "stock"}-${index}`,
        };
    });

    const SECTOR_SELECTED_STOCK_SET = new Set([
        "UNOMINDA", "BAJAJ-AUTO", "TIINDIA", "BOSCHLTD", "SONACOMS", "TVSMOTOR", "EICHERMOT", "HEROMOTOCO", "M&M", "BHARATFORG",
        "FORCEMOTOR", "PFC", "RECLTD", "BAJAJFINSV", "MFSL", "LICHSGFIN", "JIOFIN", "CHOLAIFIN", "SHRIRAMFIN", "MUTHOOTFIN",
        "BSE", "SBIN", "BAJAJFINANCE", "DABUR", "TATACONSUM", "HINDUNILVR", "GODREJCP", "UNITEDSPR", "NESTLEIND", "ITC",
        "VBL", "UBL", "BRITANNIA", "COFORGE", "PERSISTENT", "LTM", "INFY", "HCLTECH", "TECHMM", "MPHASIS", "TCS", "OFSS",
        "NATIONALUM", "HINDCOPPER", "HINDZINC", "ADANIENT", "JSWSTEEL", "JINDALSTEL", "HINDALCO", "APLAPOLLO", "TATASTEEL",
        "VEDL", "MANKIND", "LAURUSLABS", "SUNPHARMA", "TORNTPHARM", "AUROPHARMA", "DIVISLAB", "GLENMARK", "ALKEM", "BIOCON",
        "DRREDDY", "CIPLA", "LUPIN", "OBEROIRLTY", "PRESTIGE", "GODREJPROP", "LODHA", "DLF", "TITAN", "AMBER", "HAVELLS",
        "DIXON", "VOLTAS", "BLUESTARCO", "RELIANCE", "OIL", "ATGL", "ONGC", "BPCL", "PIDILITIND", "SRF", "PIIND", "CGPOWER",
        "NTPC", "TORNTPOWER", "JSWENERGY", "TATAPOWER", "SIEMENS", "ADANIGREEN", "ADANIENSOL", "ABB", "ADANIPOWER",
        "COALINDIA", "POWERINDIA", "HAL", "BEL", "BDL", "BHEL", "MAZDOCK", "COCHINSHIP", "DALBHARAT", "POLYCAB",
        "PNBHOUSING", "DMART", "360ONE", "ETERNAL", "ASIANPAINT", "LT", "HDFCBANK", "BANDHANBNK", "INDUSINDBK", "TECHM",
        "KOTAKBANK", "ICICIBANK", "AXISBANK"
    ]);

export default function StockScanner() {
    const stockScannerRef = useRef(null);
    const stickyTopShellRef = useRef(null);
    const { options, sectorialIndicesData, foTopGainerLooserData,
        atlasScannerData, stockPreOpenData, stockAnalysisData
     } = useStockApiWebsocket(
        environment.stockApiDayRangeDataWSUrl
    );

    const [sectorData, setSectorData] = useState([]);
    const [midCrossScannerData, setMidCrossScannerData] = useState({});
    const [activeTab, setActiveTab] = useState(POD_TAB);
    const [preOpenChart, setPreOpenChart] = useState({
        isOpen: false,
        isLoading: false,
        symbol: "",
        data: null,
        error: "",
    });
    const [expandedPreOpenRowKey, setExpandedPreOpenRowKey] = useState(null);

    const closePreOpenChart = () => {
        setPreOpenChart((currentChart) => ({
            ...currentChart,
            isOpen: false,
        }));
    };

    const openPreOpenChart = async (row) => {
        setPreOpenChart({
            isOpen: true,
            isLoading: true,
            symbol: row.symbol,
            data: null,
            error: "",
        });

        try {
            const query = new URLSearchParams({
                index: row.chartIndex || `${row.symbol}EQN`,
                preopen: "true",
            });
            const response = await fetch(`${STOCK_PRE_OPEN_CHART_DATA_URL}?${query.toString()}`);

            if (!response.ok) {
                throw new Error(`Chart request failed (${response.status})`);
            }

            const payload = await response.json();

            if (payload?.status === false) {
                throw new Error(payload?.message || "Unable to load chart data.");
            }

            const chartData = payload?.data ?? payload;
            const graphData = (Array.isArray(chartData?.grapthData)
                ? chartData.grapthData
                : []).filter((point) =>
                    Array.isArray(point) &&
                    Number.isFinite(Number(point[0])) &&
                    Number.isFinite(Number(point[1]))
                );

            setPreOpenChart((currentChart) => currentChart.symbol === row.symbol
                ? {
                    ...currentChart,
                    isLoading: false,
                    data: { ...chartData, grapthData: graphData },
                    error: graphData.length === 0 ? "No pre-open chart data available." : "",
                }
                : currentChart
            );
        } catch (error) {
            setPreOpenChart((currentChart) => currentChart.symbol === row.symbol
                ? {
                    ...currentChart,
                    isLoading: false,
                    error: error?.message || "Unable to load chart data.",
                }
                : currentChart
            );
        }
    };

    useEffect(() => {
        if (!preOpenChart.isOpen) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === "Escape") closePreOpenChart();
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [preOpenChart.isOpen]);

    useEffect(() => {
        setSectorData(Array.isArray(sectorialIndicesData) ? sectorialIndicesData : []);

        if (Array.isArray(sectorialIndicesData) && sectorialIndicesData.length > 0) {
            console.log("Sector Data:", sectorialIndicesData);
        }
    }, [sectorialIndicesData]);

    useEffect(() => {
        const stockScannerElement = stockScannerRef.current;
        const stickyTopShellElement = stickyTopShellRef.current;

        if (!stockScannerElement || !stickyTopShellElement) {
            return undefined;
        }

        const updateLayoutMetrics = () => {
            const stickyTopHeight = Math.ceil(stickyTopShellElement.getBoundingClientRect().height);
            const viewportHeight = Math.ceil(window.visualViewport?.height ?? window.innerHeight);

            stockScannerElement.style.setProperty("--stock-scanner-fixed-top-height", `${stickyTopHeight}px`);
            stockScannerElement.style.setProperty("--stock-scanner-viewport-height", `${viewportHeight}px`);
        };

        updateLayoutMetrics();

        const resizeObserver = typeof ResizeObserver !== "undefined"
            ? new ResizeObserver(updateLayoutMetrics)
            : null;

        resizeObserver?.observe(stickyTopShellElement);
        window.addEventListener("resize", updateLayoutMetrics);
        window.addEventListener("orientationchange", updateLayoutMetrics);
        window.visualViewport?.addEventListener("resize", updateLayoutMetrics);
        window.visualViewport?.addEventListener("scroll", updateLayoutMetrics);

        return () => {
            resizeObserver?.disconnect();
            window.removeEventListener("resize", updateLayoutMetrics);
            window.removeEventListener("orientationchange", updateLayoutMetrics);
            window.visualViewport?.removeEventListener("resize", updateLayoutMetrics);
            window.visualViewport?.removeEventListener("scroll", updateLayoutMetrics);
        };
    }, []);

    useEffect(() => {
        setMidCrossScannerData(
            atlasScannerData && typeof atlasScannerData === "object" && !Array.isArray(atlasScannerData)
                ? atlasScannerData
                : {}
        );

        if (
            atlasScannerData &&
            typeof atlasScannerData === "object" &&
            !Array.isArray(atlasScannerData) &&
            Object.keys(atlasScannerData).length > 0
        ) {
            console.log("Mid Cross Scanner Data:", atlasScannerData);
        }
    }, [atlasScannerData]);

    const getChangeCellClassName = (changePercent) => {
        if (changePercent > 0) return "positive";
        if (changePercent < 0) return "negative";
        return "neutral";
    };

    const getTopAndBottomCHP = (sectors) => {
        if (!Array.isArray(sectors) || sectors.length === 0) return {};

        const sorted = sectors
            .map((sector) => ({
                symbol: sector?.index,
                chp: toNumber(sector?.pChange),
            }))
            .filter((sector) => sector.chp != null)
            .sort((a, b) => a.chp - b.chp);

        if (sorted.length === 0) return {};

        return {
            firstLowest: sorted[0],
            secondLowest: sorted[1],
            thirdLowest: sorted[2],
            firstHighest: sorted[sorted.length - 1],
            secondHighest: sorted[sorted.length - 2],
            thirdHighest: sorted[sorted.length - 3],
        };
    };

    const getScannerHeaderSymbol = (value) =>
        String(value ?? "")
            .split(":")
            .pop()
            ?.split("-")[0]
            ?.trim() || "--";

    const getChpColor = (value) => {
        const num = toNumber(value);
        if (num == null) return "white";
        if (num > 0) return "#0dc80d";
        if (num < 0) return "rgb(228, 67, 136)";
        return "white";
    };

    const stockDayRangeList = Array.isArray(options?.stockDayRangeModelList)
        ? options.stockDayRangeModelList
        : [];
    const lotSizeBySymbol = createLotSizeBySymbolMap(stockDayRangeList);
    const hasMidCrossData = hasScannerEntries(midCrossScannerData);
    const allSectorDetailsBySymbol = createSectorDetailsMap(sectorData, () => true, false);
    const midCrossBuyRows = getScannerRowsByKey(midCrossScannerData, MID_CROSS_BUY);
    const midCrossSellRows = getScannerRowsByKey(midCrossScannerData, MID_CROSS_SELL);
    const midCrossBuyStocks = attachSectorDetailsToStocks(
        normalizeScannerStocks(midCrossBuyRows, lotSizeBySymbol),
        allSectorDetailsBySymbol,
        false
    );
    const midCrossSellStocks = attachSectorDetailsToStocks(
        normalizeScannerStocks(midCrossSellRows, lotSizeBySymbol),
        allSectorDetailsBySymbol,
        false
    );
    const foTopGainerRows = getFoTopRowsByKey(foTopGainerLooserData, "FO_TOP_GAINERS");
    const foTopLooserRows = getFoTopRowsByKey(foTopGainerLooserData, "FO_TOP_LOOSERS");
    const foTopGainerSymbolSet = createSymbolSet(foTopGainerRows);
    const foTopLooserSymbolSet = createSymbolSet(foTopLooserRows);
    const rankedSectors = getRankedSectors(sectorData);
    const highestThreeSectors = rankedSectors.slice(-3).map(({ sector }) => sector);
    const lowestThreeSectors = rankedSectors.slice(0, 3).map(({ sector }) => sector);
    const highestThreeSectorSymbolSet = createSectorSymbolSet(highestThreeSectors);
    const lowestThreeSectorSymbolSet = createSectorSymbolSet(lowestThreeSectors);
    const highestThreeSectorNameSet = createSectorNameSet(highestThreeSectors);
    const lowestThreeSectorNameSet = createSectorNameSet(lowestThreeSectors);
    const mcBuyFilterStocks = filterStocksByFoTopAndSectors(
        midCrossBuyStocks,
        foTopGainerSymbolSet,
        highestThreeSectorSymbolSet,
        highestThreeSectorNameSet
    );
    const mcSellFilterStocks = filterStocksByFoTopAndSectors(
        midCrossSellStocks,
        foTopLooserSymbolSet,
        lowestThreeSectorSymbolSet,
        lowestThreeSectorNameSet
    );
    const stockLtpBySymbol = createStockLtpBySymbolMap(stockAnalysisData);
    const stockPreOpenRows = normalizeStockPreOpenRows(stockPreOpenData).map((row) => ({
        ...row,
        ltp: stockLtpBySymbol.get(normalizeSymbol(row.symbol)),
    }));

    const midCrossBuyCount = midCrossBuyStocks.length;
    const midCrossSellCount = midCrossSellStocks.length;
    const mcFilterCount = mcBuyFilterStocks.length + mcSellFilterStocks.length;
    const stockPreOpenCount = stockPreOpenRows.length;
    const activeFixedTitle = activeTab === MID_CROSS_BUY_TAB
        ? {
            label: SCANNER_SECTION_TITLE_MAP[MID_CROSS_BUY],
            count: midCrossBuyCount,
        }
        : activeTab === MID_CROSS_SELL_TAB
            ? {
                label: SCANNER_SECTION_TITLE_MAP[MID_CROSS_SELL],
                count: midCrossSellCount,
            }
            : activeTab === POD_TAB
                ? {
                    label: SCANNER_SECTION_TITLE_MAP[POD_TAB],
                    count: stockPreOpenCount,
                }
            : null;

    useEffect(() => {
        const stockScannerElement = stockScannerRef.current;
        const stickyTopShellElement = stickyTopShellRef.current;

        if (!stockScannerElement || !stickyTopShellElement) {
            return undefined;
        }

        const animationFrameId = window.requestAnimationFrame(() => {
            const stickyTopHeight = Math.ceil(stickyTopShellElement.getBoundingClientRect().height);
            const viewportHeight = Math.ceil(window.visualViewport?.height ?? window.innerHeight);

            stockScannerElement.style.setProperty("--stock-scanner-fixed-top-height", `${stickyTopHeight}px`);
            stockScannerElement.style.setProperty("--stock-scanner-viewport-height", `${viewportHeight}px`);
        });

        return () => window.cancelAnimationFrame(animationFrameId);
    }, [activeTab, midCrossBuyCount, midCrossSellCount, mcFilterCount, stockPreOpenCount]);
    const sectorHeaderData = sectorData[0] ?? {};
    const headerDate = sectorHeaderData?.localDate ?? sectorHeaderData?.createdDate ?? sectorHeaderData?.date ?? "--";
    const headerTime = sectorHeaderData?.localTime ?? sectorHeaderData?.createdTime ?? sectorHeaderData?.time ?? "--";
    const scannerHeaderStats = getTopAndBottomCHP(sectorData);
    const topBottomHeaderData = [
        {
            label1: "1st HS",
            value1: getScannerHeaderSymbol(scannerHeaderStats?.firstHighest?.symbol),
            label2: "CHP",
            value2: scannerHeaderStats?.firstHighest?.chp ?? "--",
        },
        {
            label1: "2nd HS",
            value1: getScannerHeaderSymbol(scannerHeaderStats?.secondHighest?.symbol),
            label2: "CHP",
            value2: scannerHeaderStats?.secondHighest?.chp ?? "--",
        },
        {
            label1: "3rd HS",
            value1: getScannerHeaderSymbol(scannerHeaderStats?.thirdHighest?.symbol),
            label2: "CHP",
            value2: scannerHeaderStats?.thirdHighest?.chp ?? "--",
        },
        {
            label1: "1st LS",
            value1: getScannerHeaderSymbol(scannerHeaderStats?.firstLowest?.symbol),
            label2: "CHP",
            value2: scannerHeaderStats?.firstLowest?.chp ?? "--",
        },
        {
            label1: "2nd LS",
            value1: getScannerHeaderSymbol(scannerHeaderStats?.secondLowest?.symbol),
            label2: "CHP",
            value2: scannerHeaderStats?.secondLowest?.chp ?? "--",
        },
        {
            label1: "3rd LS",
            value1: getScannerHeaderSymbol(scannerHeaderStats?.thirdLowest?.symbol),
            label2: "CHP",
            value2: scannerHeaderStats?.thirdLowest?.chp ?? "--",
        },
    ];
    const renderTableRows = (stocks, emptyMessage, showCount = true, highlightedSymbolSet = new Set()) => {
        if (stocks.length === 0) {
            return (
                <tr>
                    <td colSpan={showCount ? "10" : "9"} className="stock-scanner-empty-cell">
                        {emptyMessage}
                    </td>
                </tr>
            );
        }

        return stocks.map((stock) => {
            const changePercentValue = stock.changePercent ?? 0;
            const isPositive = changePercentValue >= 0;
            const normalizedSymbol = normalizeSymbol(stock.symbol);
            const isHighlightedSymbol = highlightedSymbolSet.has(normalizedSymbol);
            const sectorPercentValue = toNumber(stock.sectorPercent);

            return (
                <tr key={stock.__scannerKey}>
                    <td>{stock.triggerDate}</td>
                    <td>{stock.triggerTime}</td>
                    <td className={isHighlightedSymbol ? "symbol-highlight" : ""}>{stock.symbol}</td>
                    <td>{formatNumber(stock.ltp)}</td>
                    <td className={getChangeCellClassName(changePercentValue)}>
                        {isPositive ? "+" : ""}
                        {formatNumber(changePercentValue)}%
                    </td>
                    {showCount && <td>{stock.symbolCount}</td>}
                    <td>{stock.sectorName ?? "--"}</td>
                    <td className={sectorPercentValue == null ? "" : getChangeCellClassName(sectorPercentValue)}>
                        {sectorPercentValue == null
                            ? "--"
                            : `${sectorPercentValue >= 0 ? "+" : ""}${formatNumber(sectorPercentValue)}%`}
                    </td>
                    <td>{stock.fnoLotSize ?? "--"}</td>
                    <td>{formatNumber(stock.volume)}</td>
                </tr>
            );
        });
    };

    const scannerWaitingMessage = hasMidCrossData
        ? "No matching stocks found."
        : "Waiting for live scanner data...";
    const hasSectorData = sectorData.length > 0;
    const mcBuyFilterWaitingMessage = !hasMidCrossData
        ? scannerWaitingMessage
        : foTopGainerRows.length === 0
            ? "Waiting for live F&O top gainers data..."
            : !hasSectorData
                ? "Waiting for live sector data..."
                : "No matching stocks found.";
    const mcSellFilterWaitingMessage = !hasMidCrossData
        ? scannerWaitingMessage
        : foTopLooserRows.length === 0
            ? "Waiting for live F&O top loosers data..."
            : !hasSectorData
                ? "Waiting for live sector data..."
                : "No matching stocks found.";

    const renderScannerTable = (
        stocks,
        emptyMessage,
        showCount = true,
        highlightedSymbolSet = new Set(),
        useContentHeight = false
    ) => (
        <div className={`table-scroll-area ${useContentHeight ? "mc-filter-table-scroll-area" : ""}`}>
            <table className="responsive-table table-bordered text-center sticky-header stock-day-range-table scanner-table">
                <thead>
                    <tr>
                        <th>DATE</th>
                        <th>TIME</th>
                        <th>SYMBOL</th>
                        <th>LTP</th>
                        <th>CHANGE (%)</th>
                        {showCount && <th>COUNT</th>}
                        <th>SECTOR</th>
                        <th>SECTOR (%)</th>
                        <th>FNO LOT SIZE</th>
                        <th>VOLUME</th>
                    </tr>
                </thead>
                <tbody>{renderTableRows(stocks, emptyMessage, showCount, highlightedSymbolSet)}</tbody>
            </table>
        </div>
    );

    const renderScannerSection = (
        sectionKey,
        stocks,
        emptyMessage = scannerWaitingMessage,
        showTitle = true,
        useContentHeight = false
    ) => (
        <div className="scanner-section-list">
            <div className="scanner-section-block">
                {showTitle && (
                    <div className="scanner-section-title">
                        {SCANNER_SECTION_TITLE_MAP[sectionKey]} ({stocks.length})
                    </div>
                )}
                {renderScannerTable(stocks, emptyMessage, false, new Set(), useContentHeight)}
            </div>
        </div>
    );

    const renderPreOpenBook = (row, columnCount) => {
        const preOpenMarket = row.preOpenMarket ?? {};
        const orderRows = Array.isArray(preOpenMarket.preopen)
            ? preOpenMarket.preopen
            : Array.isArray(preOpenMarket.preOpen)
                ? preOpenMarket.preOpen
                : [];
        const ato = preOpenMarket.ato ?? {};
        const totalBuyQuantity =
            preOpenMarket.totalBuyQuantity ?? preOpenMarket.totalBuyQty;
        const totalSellQuantity =
            preOpenMarket.totalSellQuantity ?? preOpenMarket.totalSellQty;
        const equilibriumPrice =
            preOpenMarket.IEP ?? preOpenMarket.iep ?? row.iep;
        const equilibriumQuantity =
            preOpenMarket.indicativeEquilibriumQuantity ??
            preOpenMarket.iepQuantity ??
            preOpenMarket.totalTradedVolume ??
            row.finalQuantity;
        const changeValue = toNumber(row.change);
        const pChangeValue = toNumber(row.pChange);
        const summaryRows = [
            ["Indicative Equilibrium Price (In ₹)", formatPreOpenPrice(equilibriumPrice)],
            ["Indicative Equilibrium Quantity", formatPreOpenQuantity(equilibriumQuantity)],
            ["Final Price (In ₹)", formatPreOpenPrice(row.finalPrice)],
            ["Final Quantity", formatPreOpenQuantity(row.finalQuantity)],
            ["Previous Close", formatPreOpenPrice(row.previousClose)],
            ["Change", formatPreOpenPrice(changeValue), getChangeCellClassName(changeValue)],
            ["%Change", pChangeValue == null ? "-" : formatPreOpenNumber(pChangeValue),
                getChangeCellClassName(pChangeValue)],
        ];

        return (
            <tr className="pod-pre-open-details-row">
                <td colSpan={columnCount} className="pod-pre-open-details-cell">
                    <div className="pod-pre-open-book">
                        <div className="pod-pre-open-book-title">
                            {row.symbol}
                        </div>
                        <div className="pod-pre-open-book-grid">
                            <table className="pod-book-table">
                                <thead>
                                    <tr>
                                        <th>BUY QTY</th>
                                        <th>PRICE</th>
                                        <th>SELL QTY</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orderRows.length > 0 ? orderRows.map((order, index) => {
                                        const orderPrice =
                                            order?.price ?? order?.orderPrice ?? order?.value;
                                        const isEquilibriumPrice = Boolean(order?.iep) ||
                                            (toNumber(orderPrice) != null &&
                                                toNumber(orderPrice) === toNumber(equilibriumPrice));

                                        return (
                                            <tr key={`${row.key}-book-${index}`}>
                                                <td>{formatPreOpenQuantity(
                                                    order?.buyQty ?? order?.buyQuantity ?? order?.buy
                                                )}</td>
                                                <td className={isEquilibriumPrice ? "pod-book-iep-price" : "pod-book-price"}>
                                                    {formatPreOpenPrice(orderPrice)}
                                                </td>
                                                <td>{formatPreOpenQuantity(
                                                    order?.sellQty ?? order?.sellQuantity ?? order?.sell
                                                )}</td>
                                            </tr>
                                        );
                                    }) : (
                                        <tr>
                                            <td colSpan="3" className="pod-book-empty-cell">
                                                No pre-open order-book data available.
                                            </td>
                                        </tr>
                                    )}
                                    <tr className="pod-book-footer-heading">
                                        <th>BUY QTY</th>
                                        <th aria-label="Order type" />
                                        <th>SELL QTY</th>
                                    </tr>
                                    <tr>
                                        <td>{formatPreOpenQuantity(
                                            ato?.buy ?? ato?.buyQty ?? preOpenMarket.atoBuyQty
                                        )}</td>
                                        <td className="pod-book-price">At the Open (ATO)</td>
                                        <td>{formatPreOpenQuantity(
                                            ato?.sell ?? ato?.sellQty ?? preOpenMarket.atoSellQty
                                        )}</td>
                                    </tr>
                                    <tr>
                                        <td>{formatPreOpenQuantity(totalBuyQuantity)}</td>
                                        <td className="pod-book-price">Total</td>
                                        <td>{formatPreOpenQuantity(totalSellQuantity)}</td>
                                    </tr>
                                </tbody>
                            </table>

                            <table className="pod-summary-table">
                                <thead>
                                    <tr>
                                        <th colSpan="2">ORDER COLLECTION SUMMARY</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {summaryRows.map(([label, value, valueClassName = ""]) => (
                                        <tr key={label}>
                                            <td>{label}</td>
                                            <td className={valueClassName}>{value}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </td>
            </tr>
        );
    };

    const renderPreOpenTable = (
        rows = stockPreOpenRows,
        useContentHeight = false,
        emptyMessage = "Waiting for stock pre-open data...",
        showOpenPrice = false,
        showChart = true
    ) => {
        const columnCount = 10 + Number(showOpenPrice) + Number(showChart);

        return (
        <div className={`table-scroll-area ${useContentHeight ? "mc-filter-table-scroll-area" : ""}`}>
            <table className="responsive-table table-bordered text-center sticky-header scanner-table pod-table">
                <thead>
                    <tr>
                        <th>SYMBOL</th>
                        {showChart && <th>CHART</th>}
                        <th>BUY QTY</th>
                        <th>SELL QTY</th>
                        <th>SIGNAL</th>
                        <th>PREV. CLOSE</th>
                        <th>FINAL PRICE</th>
                        <th>LTP</th>
                        {showOpenPrice && <th>OPEN PRICE</th>}
                        <th>CHNG</th>
                        <th>%CHNG</th>
                        <th>FINAL QTY</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length > 0 ? (
                        rows.map((row) => {
                            const changeValue = toNumber(row.change);
                            const pChangeValue = toNumber(row.pChange);
                            const isExpanded = expandedPreOpenRowKey === row.key;
                            const ltpValue = toNumber(row.ltp);
                            const finalPriceValue = toNumber(row.finalPrice);
                            const previousCloseValue = toNumber(row.previousClose);
                            const ltpComparisonClass =
                                ltpValue == null ||
                                finalPriceValue == null ||
                                previousCloseValue == null
                                ? ""
                                : ltpValue > finalPriceValue && ltpValue > previousCloseValue
                                    ? "pod-ltp-above-final"
                                    : ltpValue < finalPriceValue && ltpValue < previousCloseValue
                                        ? "pod-ltp-below-final"
                                        : "";
                            const buyQuantity =
                                row.preOpenMarket?.totalBuyQuantity ??
                                row.preOpenMarket?.totalBuyQty ??
                                row.buyQty ??
                                row.buyQuantity;
                            const sellQuantity =
                                row.preOpenMarket?.totalSellQuantity ??
                                row.preOpenMarket?.totalSellQty ??
                                row.sellQty ??
                                row.sellQuantity;
                            const buyQuantityValue = toNumber(buyQuantity) ?? 0;
                            const sellQuantityValue = toNumber(sellQuantity) ?? 0;
                            const podSignal = buyQuantityValue > sellQuantityValue
                                ? "BUY"
                                : buyQuantityValue < sellQuantityValue
                                    ? "SELL"
                                    : "NO SIGNAL";
                            const podSignalClassName = podSignal === "BUY"
                                ? "pod-signal-buy"
                                : podSignal === "SELL"
                                    ? "pod-signal-sell"
                                    : "pod-signal-neutral";

                            return (
                                <React.Fragment key={row.key}>
                                <tr>
                                    <td>
                                        <button
                                            type="button"
                                            className="pod-symbol-button"
                                            aria-expanded={isExpanded}
                                            onClick={() => setExpandedPreOpenRowKey(
                                                isExpanded ? null : row.key
                                            )}
                                        >
                                            {row.symbol}
                                        </button>
                                    </td>
                                    {showChart && (
                                        <td>
                                            <button
                                                type="button"
                                                className="pod-chart-button"
                                                aria-label={`Open ${row.symbol} pre-open chart`}
                                                title={`Open ${row.symbol} chart`}
                                                onClick={() => openPreOpenChart(row)}
                                            >
                                                <ChartLine aria-hidden="true" />
                                            </button>
                                        </td>
                                    )}
                                    <td>{formatPreOpenQuantity(buyQuantity)}</td>
                                    <td>{formatPreOpenQuantity(sellQuantity)}</td>
                                    <td>
                                        <span className={`pod-signal ${podSignalClassName}`}>
                                            {podSignal}
                                        </span>
                                    </td>
                                    <td>{formatPreOpenNumber(row.previousClose)}</td>
                                    <td>{formatPreOpenNumber(row.finalPrice)}</td>
                                    <td className={ltpComparisonClass}>
                                        {formatPreOpenNumber(row.ltp)}
                                    </td>
                                    {showOpenPrice && (
                                        <td>{formatPreOpenNumber(row.openPrice)}</td>
                                    )}
                                    <td className={getChangeCellClassName(changeValue)}>
                                        {changeValue > 0 ? "+" : ""}{formatPreOpenNumber(changeValue)}
                                    </td>
                                    <td className={getChangeCellClassName(pChangeValue)}>
                                        {pChangeValue > 0 ? "+" : ""}{formatPreOpenNumber(pChangeValue)}%
                                    </td>
                                    <td>{formatPreOpenNumber(row.finalQuantity, 0)}</td>
                                </tr>
                                {isExpanded && renderPreOpenBook(row, columnCount)}
                                </React.Fragment>
                            );
                        })
                    ) : (
                        <tr>
                            <td
                                colSpan={columnCount}
                                className="stock-scanner-empty-cell"
                            >
                                {emptyMessage}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
        );
    };

    const preOpenChartPriceRange = getPreOpenChartPriceRange(
        preOpenChart.data?.grapthData
    );

    return (
        <div className="stock-scanner" ref={stockScannerRef}>
            <div className="sticky-top-shell" ref={stickyTopShellRef}>
                <header className="header">
                    <nav className="nav-container">
                        <div className="header-flex">
                            <div className="left-nav">
                                <div className="logo-container">
                                    <img
                                        src="/assets/images/noptionwave-logo-1.png"
                                        className="header-logo"
                                        alt="logo"
                                    />
                                </div>

                                {topBottomHeaderData.map((item, idx) => {
                                    const color = getChpColor(item.value2);

                                    return (
                                        <div className="header-item" key={`${item.label1}-${idx}`}>
                                            <span className="header-line" style={{ color }}>
                                                {item.label1} : {item.value1}
                                            </span>
                                            <span className="header-line" style={{ color }}>
                                                {item.label2} : {item.value2}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="right-nav">
                                <span className="header-line" style={{ color: "white" }}>
                                    Date : {headerDate}
                                </span>
                                <span className="header-line" style={{ color: "white" }}>
                                    Time : {headerTime}
                                </span>
                            </div>
                        </div>
                    </nav>
                </header>

                <div className="tab-container">
                    <div className="tab">
                        <button
                            className={`tablinks sub-header-font ${activeTab === POD_TAB ? "active" : ""}`}
                            onClick={() => setActiveTab(POD_TAB)}
                        >
                            POD ({stockPreOpenCount})
                        </button>
                        <button
                            className={`tablinks sub-header-font ${activeTab === MID_CROSS_BUY_TAB ? "active" : ""}`}
                            onClick={() => setActiveTab(MID_CROSS_BUY_TAB)}
                        >
                            MID CROSS BUY ({midCrossBuyCount})
                        </button>
                        <button
                            className={`tablinks sub-header-font ${activeTab === MC_FILTER_TAB ? "active" : ""}`}
                            onClick={() => setActiveTab(MC_FILTER_TAB)}
                        >
                            MC FILTER ({mcFilterCount})
                        </button>
                        <button
                            className={`tablinks sub-header-font ${activeTab === MID_CROSS_SELL_TAB ? "active" : ""}`}
                            onClick={() => setActiveTab(MID_CROSS_SELL_TAB)}
                        >
                            MID CROSS SELL ({midCrossSellCount})
                        </button>
                    </div>
                </div>

                {activeFixedTitle && (
                    <div className="fixed-active-scanner-title">
                        {activeFixedTitle.label} ({activeFixedTitle.count})
                    </div>
                )}
            </div>

            <div className="table-container">
                {activeTab === MID_CROSS_BUY_TAB && renderScannerSection(
                    MID_CROSS_BUY,
                    midCrossBuyStocks,
                    scannerWaitingMessage,
                    false
                )}

                {activeTab === MID_CROSS_SELL_TAB && renderScannerSection(
                    MID_CROSS_SELL,
                    midCrossSellStocks,
                    scannerWaitingMessage,
                    false
                )}

                {activeTab === POD_TAB && renderPreOpenTable()}

                {activeTab === MC_FILTER_TAB && (
                    <>
                        {renderScannerSection(
                            MC_BUY_FILTER_TAB,
                            mcBuyFilterStocks,
                            mcBuyFilterWaitingMessage,
                            true,
                            true
                        )}

                        {renderScannerSection(
                            MC_SELL_FILTER_TAB,
                            mcSellFilterStocks,
                            mcSellFilterWaitingMessage,
                            true,
                            true
                        )}
                    </>
                )}
            </div>

            <div className="fixed-footer">
                <div className="footer-left">
                    <span>noptionwave © 2026</span>
                </div>
            </div>

            {preOpenChart.isOpen && (
                <div
                    className="pod-chart-modal-backdrop"
                    role="presentation"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) closePreOpenChart();
                    }}
                >
                    <section
                        className="pod-chart-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="pod-chart-symbol"
                    >
                        <div className="pod-chart-modal-header">
                            <h2 id="pod-chart-symbol" className="pod-chart-modal-heading">
                                <span className="pod-chart-heading-symbol">
                                    {preOpenChart.data?.identifier || preOpenChart.symbol}
                                </span>
                                {preOpenChartPriceRange.highest != null && (
                                    <span className="pod-chart-heading-high">
                                        RES: {formatPreOpenNumber(preOpenChartPriceRange.highest)}
                                    </span>
                                )}
                                {preOpenChartPriceRange.lowest != null && (
                                    <span className="pod-chart-heading-low">
                                        SUP: {formatPreOpenNumber(preOpenChartPriceRange.lowest)}
                                    </span>
                                )}
                            </h2>
                            <button
                                type="button"
                                className="pod-chart-close-button"
                                aria-label="Close chart"
                                onClick={closePreOpenChart}
                            >
                                <X aria-hidden="true" />
                            </button>
                        </div>
                        <div className="pod-chart-modal-body">
                            {preOpenChart.isLoading && (
                                <div className="pod-chart-message">Loading chart...</div>
                            )}
                            {!preOpenChart.isLoading && preOpenChart.error && (
                                <div className="pod-chart-message pod-chart-error">
                                    {preOpenChart.error}
                                </div>
                            )}
                            {!preOpenChart.isLoading && !preOpenChart.error && preOpenChart.data && (() => {
                                const chartPoints = preOpenChart.data.grapthData
                                    .map(([timestamp, price]) => ({
                                        timestamp: Number(timestamp),
                                        price: Number(price),
                                    }))
                                    .filter((point) =>
                                        Number.isFinite(point.timestamp) && Number.isFinite(point.price)
                                    )
                                    .sort((firstPoint, secondPoint) =>
                                        firstPoint.timestamp - secondPoint.timestamp
                                    );
                                const timeFormatter = new Intl.DateTimeFormat("en-IN", {
                                    timeZone: "UTC",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: false,
                                });
                                const priceValues = chartPoints.map((point) => point.price);
                                const minimumPrice = Math.min(...priceValues);
                                const maximumPrice = Math.max(...priceValues);
                                const priceRange = Math.max(maximumPrice - minimumPrice, 1);
                                const roughTickStep = priceRange / 4;
                                const tickMagnitude = 10 ** Math.floor(Math.log10(roughTickStep));
                                const normalizedTickStep = roughTickStep / tickMagnitude;
                                const niceTickMultiplier = normalizedTickStep <= 1
                                    ? 1
                                    : normalizedTickStep <= 2
                                        ? 2
                                        : normalizedTickStep <= 5
                                            ? 5
                                            : 10;
                                const yTickStep = niceTickMultiplier * tickMagnitude;
                                const roundedYAxisMinimum =
                                    Math.floor(minimumPrice / yTickStep) * yTickStep;
                                const yAxisMinimum = roundedYAxisMinimum >= minimumPrice
                                    ? roundedYAxisMinimum - yTickStep
                                    : roundedYAxisMinimum;
                                const yAxisMaximum = Math.ceil(maximumPrice / yTickStep) * yTickStep;
                                const chartData = {
                                    datasets: [{
                                        data: chartPoints.map((point) => ({
                                            x: point.timestamp,
                                            y: point.price,
                                        })),
                                        borderColor: PRE_OPEN_CHART_LINE_COLOR,
                                        backgroundColor: (context) => {
                                            const { chart } = context;
                                            const { ctx, chartArea } = chart;

                                            if (!chartArea) return PRE_OPEN_CHART_FILL_COLOR;

                                            const gradient = ctx.createLinearGradient(
                                                0,
                                                chartArea.top,
                                                0,
                                                chartArea.bottom
                                            );
                                            gradient.addColorStop(0, "rgba(255, 177, 67, 0.42)");
                                            gradient.addColorStop(1, "rgba(255, 245, 226, 0.18)");
                                            return gradient;
                                        },
                                        borderWidth: 4,
                                        borderCapStyle: "round",
                                        borderJoinStyle: "round",
                                        fill: true,
                                        pointRadius: 0,
                                        pointHoverRadius: 4,
                                        tension: 0,
                                    }],
                                };
                                const chartOptions = {
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    animation: false,
                                    parsing: false,
                                    normalized: true,
                                    interaction: { intersect: false, mode: "index" },
                                    plugins: {
                                        legend: { display: false },
                                        tooltip: {
                                            displayColors: false,
                                            titleFont: { size: 20, weight: "bold" },
                                            bodyFont: { size: 20, weight: "bold" },
                                            padding: 10,
                                            callbacks: {
                                                title: ([context]) => context
                                                    ? `Time: ${timeFormatter.format(new Date(context.parsed.x))}`
                                                    : "",
                                                label: (context) => [
                                                    `Symbol: ${preOpenChart.data?.identifier || preOpenChart.symbol}`,
                                                    `Price: ${formatPreOpenNumber(context.parsed.y)}`,
                                                ],
                                            },
                                        },
                                    },
                                    scales: {
                                        x: {
                                            type: "linear",
                                            min: chartPoints[0]?.timestamp,
                                            max: chartPoints[chartPoints.length - 1]?.timestamp,
                                            grid: { display: false },
                                            ticks: {
                                                color: "#353535",
                                                stepSize: 2 * 60 * 1000,
                                                font: { size: 28 },
                                                callback: (value) => timeFormatter.format(new Date(value)),
                                            },
                                            afterBuildTicks: (axis) => {
                                                axis.ticks = axis.ticks.filter((tick) =>
                                                    tick.value > axis.min && tick.value < axis.max
                                                );
                                            },
                                            border: { color: "#333333", width: 2 },
                                        },
                                        y: {
                                            min: yAxisMinimum,
                                            max: yAxisMaximum === yAxisMinimum
                                                ? yAxisMinimum + yTickStep
                                                : yAxisMaximum,
                                            grid: { display: false },
                                            ticks: {
                                                color: "#312e81",
                                                stepSize: yTickStep,
                                                font: { size: 28 },
                                                padding: 20,
                                                callback: (value) => Number(value).toLocaleString("en-IN", {
                                                    useGrouping: false,
                                                    maximumFractionDigits: 2,
                                                }),
                                            },
                                            border: { display: false },
                                        },
                                    },
                                };

                                return (
                                    <div className="pod-chart-canvas-wrap">
                                        <Line data={chartData} options={chartOptions} />
                                    </div>
                                );
                            })()}
                        </div>
                    </section>
                </div>
            )}
        </div>
    );
}
